// src/components/AddGroupPage.js
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext'; // To check if user is logged in
import { functions } from '../firebase'; // Your initialized Firebase functions instance
import { httpsCallable } from 'firebase/functions';
import { Container, Row, Col, Card, Form, Button, Spinner, Alert, Breadcrumb } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSpinner, faExclamationTriangle, faCheckCircle } from '@fortawesome/free-solid-svg-icons';

// Define the callable function reference
const addGroupCallable = httpsCallable(functions, 'addGroup');

function AddGroupPage() {
    const [groupName, setGroupName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const navigate = useNavigate();
    const { currentUser } = useAuth(); // Get user for auth check

    const validateForm = () => {
        setError(null);
        if (!groupName.trim()) {
            setError("Group Name cannot be empty.");
            return false;
        }
        // Add any other client-side validation if needed (e.g., length)
        return true;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validateForm() || !currentUser) {
            if (!currentUser) setError("You must be logged in to add a group.");
            return;
        }

        setIsLoading(true);
        setError(null);
        setSuccessMessage(null);

        const payload = {
            groupName: groupName.trim(),
            // orgId is NOT sent from client, backend gets it from claims
        };

        console.log("Calling 'addGroup' function with payload:", payload);

        try {
            const result = await addGroupCallable(payload);
            const resultData = result.data; // Define interface if needed { success, message, groupId }
            console.log("'addGroup' function result:", resultData);

            if (resultData?.success) {
                setSuccessMessage(resultData.message || "Group created successfully!");
                setGroupName(''); // Clear form
                // Redirect back to the groups page after a short delay
                setTimeout(() => {
                    navigate('/groups', { state: { message: `Group '${payload.groupName}' created.` } });
                }, 2000);
            } else {
                setError(resultData?.message || "Failed to create group.");
            }
        } catch (err) {
            console.error("Error calling 'addGroup' function:", err);
            // Handle specific Firebase Functions errors
             let specificError = "An unexpected error occurred.";
             if (err.code && err.message) {
                 if (err.code === 'permission-denied') { specificError = "You do not have permission to create groups."; }
                 else if (err.code === 'already-exists') { specificError = "A group with this name already exists in your organization."; }
                 else if (err.code === 'invalid-argument') { specificError = "Invalid data provided (e.g., group name empty)."; }
                 else { specificError = `Server error: ${err.message} (code: ${err.code})`; }
            }
            setError(specificError);
        } finally {
            // Don't set loading false on success if navigating away
            if (error || !successMessage) {
                 setIsLoading(false);
            }
        }
    };

     // Determine if form inputs should be disabled
    const formInputsDisabled = isLoading || !!successMessage;

    return (
        <Container fluid className="mt-3">
             <Row>
                <Col>
                    <Breadcrumb>
                        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/dashboard" }}>Dashboard</Breadcrumb.Item>
                        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/groups" }}>Groups</Breadcrumb.Item>
                        <Breadcrumb.Item active>Add Group</Breadcrumb.Item>
                    </Breadcrumb>
                 </Col>
            </Row>
            <Row className="justify-content-center">
                <Col md={8} lg={6}>
                    <Card className="shadow-sm">
                        <Card.Header as="h2">
                            <FontAwesomeIcon icon={faPlus} className="me-2"/> Add New Group
                        </Card.Header>
                        <Card.Body>
                            {error && ( <Alert variant="danger"><FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />{error}</Alert> )}
                            {successMessage && ( <Alert variant="success"><FontAwesomeIcon icon={faCheckCircle} className="me-2" />{successMessage}</Alert> )}

                            {!successMessage && ( // Hide form on success
                                <Form onSubmit={handleSubmit} noValidate>
                                    <Form.Group className="mb-3" controlId="groupNameInput">
                                        <Form.Label>Group Name <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter group name"
                                            value={groupName}
                                            onChange={(e) => setGroupName(e.target.value)}
                                            required
                                            disabled={formInputsDisabled}
                                            maxLength={100} // Example max length
                                        />
                                    </Form.Group>

                                    <div className="d-flex justify-content-end mt-4">
                                        <Button variant="secondary" onClick={() => navigate('/groups')} disabled={isLoading} className="me-2">
                                            Cancel
                                        </Button>
                                        <Button variant="primary" type="submit" disabled={formInputsDisabled}>
                                            {isLoading ? (
                                                <> <FontAwesomeIcon icon={faSpinner} spin className="me-2" /> Creating... </>
                                            ) : ( "Create Group" )}
                                        </Button>
                                    </div>
                                </Form>
                            )}
                            {successMessage && ( // Show button to go back on success
                                <div className="text-center mt-3">
                                    <Button variant="primary" onClick={() => navigate('/groups')}>Go to Groups</Button>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

export default AddGroupPage;